import { NEW_ALARM_TOAST_ID } from '@hakimo-ui/hakimo/feature-alarms';
import {
  UnifiedMonitoringAlarmCountMessage,
  UnifiedMonitoringCount,
  UnifiedMonitoringMessageType,
} from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import {
  audioNotifyAtom,
  dismissToast,
  systemNotifyAtom,
  toast,
  triggerSystemNotification,
  useUnifiedMonitoringWs,
} from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FETCH_ALARMS_COUNT_POLLING_INTERVAL } from './constants';
import { useUnifiedMonitoringWsMessageHandler } from './hooks/useUnifiedMonitoringWsMessageHandler';
interface Props {
  fetchAlarms: () => void;
  isLoading: boolean;
  shownAlarmsCount: number;
}

export function PendingAlarmPanel(props: Props) {
  const { fetchAlarms, isLoading, shownAlarmsCount } = props;
  const [pendingAlarmCount, setPendingAlarmCount] =
    useState<UnifiedMonitoringCount | null>(null);
  const prevPendingTotalRef = useRef(0);
  const audioNotify = useAtomValue(audioNotifyAtom);
  const systemNotifyPreference = useAtomValue(systemNotifyAtom);

  const totalCount =
    (pendingAlarmCount?.highPriority ?? 0) +
    (pendingAlarmCount?.lowPriority ?? 0);

  const { send } = useUnifiedMonitoringWs();

  const fetchAlarmsCount = useCallback(() => {
    send?.({
      event_type: UnifiedMonitoringMessageType.ALARM_COUNT,
    });
  }, [send]);

  useEffect(() => {
    fetchAlarmsCount();
    const timerId = window.setInterval(
      fetchAlarmsCount,
      FETCH_ALARMS_COUNT_POLLING_INTERVAL
    );

    return () => {
      timerId && clearInterval(timerId);
    };
  }, [fetchAlarmsCount]);

  const handleAlarmCountResponse = (
    data: UnifiedMonitoringAlarmCountMessage
  ) => {
    const currentTotal =
      data.pending_high_priority_alarm_count +
      data.pending_low_priority_alarm_count;
    const prevTotal = prevPendingTotalRef.current;
    setPendingAlarmCount({
      highPriority: data.pending_high_priority_alarm_count,
      lowPriority: data.pending_low_priority_alarm_count,
    });
    if (shownAlarmsCount === 0) {
      if (prevTotal === 0 && currentTotal > 0) {
        triggerSystemNotification(systemNotifyPreference);
        toast('New pending alarm', {
          audio: audioNotify,
          id: NEW_ALARM_TOAST_ID,
        });
      }

      if (currentTotal === 0) {
        dismissToast(NEW_ALARM_TOAST_ID);
      }
    }

    prevPendingTotalRef.current = currentTotal;
  };
  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.ALARM_COUNT,
    handleAlarmCountResponse
  );

  if (shownAlarmsCount > 0) {
    return null;
  }

  return (
    <div className="flex flex-1 p-4">
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 w-full rounded-lg">
        {pendingAlarmCount === null && (
          <div className="flex w-full items-center justify-center py-16">
            <LoadingIndicator text="Fetching pending alarms..." />
          </div>
        )}
        {pendingAlarmCount !== null && totalCount === 0 && (
          <div className="flex flex-col items-center py-8">
            <div className="text-status-green flex justify-center">
              <CheckCircleIcon className="h-48 w-48" />
            </div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2 mt-4 text-center text-4xl font-medium">
              No pending alarms!
            </div>
          </div>
        )}
        {pendingAlarmCount !== null && totalCount > 0 && (
          <div className="flex h-full flex-col items-center justify-center">
            <div className="flex justify-center text-red-500 motion-safe:animate-bounce">
              <ExclamationCircleIcon className="h-72 w-72" />
            </div>
            <div className="mt-6 flex items-center justify-center">
              <span className="rounded-l-full bg-red-100 p-3 px-6 text-red-800 dark:bg-red-700/20 dark:text-red-500">
                {pendingAlarmCount?.highPriority} High Priority
              </span>
              <span className="rounded-r-full bg-green-100 p-3 px-6 text-green-800 dark:bg-green-800/20 dark:text-green-600">
                {pendingAlarmCount?.lowPriority} Low Priority
              </span>
            </div>
            <div className="mt-6">
              <Button
                variant="primary"
                onClick={fetchAlarms}
                loading={isLoading}
                disabled={isLoading}
                className="px-12 py-4"
              >
                <div className="text-5xl">Get Alarm</div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default PendingAlarmPanel;
